import { CSSProperties } from 'react';
import { mainColors } from '../../../../../styling/theme';
import {
  BottomPanelView,
  SubRedDateValueRow,
  SubRedPanelView,
  SubscriptionsRedemptionsReport,
  TopPanelView,
} from '../subscriptionsRedemptions.types';

const LEFT_TAIL_METRICS = [
  'Median',
  '25th Percentile',
  '1st Percentile',
  'Min',
];

const RIGHT_TAIL_METRICS = [
  'Median',
  '75th Percentile',
  '99th Percentile',
  'Max',
];

export const PANEL_CARD_STYLE: CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

export type SubRedPanelConfig = {
  tableTitle: string;
  chartTitle: string;
  tableId: string;
  chartId: string;
  color: string;
  toggleLabel: string;
};

export const TOP_PANEL_VIEWS: TopPanelView[] = ['redemption', 'subscription'];
export const BOTTOM_PANEL_VIEWS: BottomPanelView[] = ['gross', 'net'];

export const SUB_RED_PANEL_CONFIG: Record<SubRedPanelView, SubRedPanelConfig> =
  {
    redemption: {
      tableTitle: 'Redemption Risk',
      chartTitle: 'Redemption Data',
      tableId: 'redemption_table',
      chartId: 'redemption_chart',
      color: mainColors.mainBlue_lighter,
      toggleLabel: 'Redemption',
    },
    subscription: {
      tableTitle: 'Subscription Risk',
      chartTitle: 'Subscription Data',
      tableId: 'subscription_table',
      chartId: 'subscription_chart',
      color: mainColors.pineGreen,
      toggleLabel: 'Subscription',
    },
    gross: {
      tableTitle: 'Gross Activity Risk',
      chartTitle: 'Gross Activity Data',
      tableId: 'gross_activity_table',
      chartId: 'gross_activity_chart',
      color: mainColors.Pass,
      toggleLabel: 'Gross',
    },
    net: {
      tableTitle: 'Net Activity Risk',
      chartTitle: 'Net Activity Data',
      tableId: 'net_activity_table',
      chartId: 'net_activity_chart',
      color: mainColors.mainGold,
      toggleLabel: 'Net',
    },
  };

function numericSeriesValues(rows: SubRedDateValueRow[] | undefined): number[] {
  if (!rows || rows.length < 2) {
    return [];
  }
  return rows.slice(1).flatMap((row) =>
    typeof row[1] === 'number' ? [row[1]] : [],
  );
}

function seriesToMap(
  rows: SubRedDateValueRow[] | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  if (!rows) {
    return map;
  }
  rows.slice(1).forEach(([date, value]) => {
    if (typeof value === 'number') {
      map.set(date, value);
    }
  });
  return map;
}

/** Linear interpolation, matching numpy percentile default. */
function percentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) {
    return 0;
  }
  const index = (p / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) {
    return sortedValues[lower];
  }
  const weight = index - lower;
  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

function sortedStats(values: number[]): number[] {
  return [...values].sort((a, b) => a - b);
}

function rightTailStats(values: number[]): number[] {
  if (values.length === 0) {
    return [];
  }
  const sorted = sortedStats(values);
  return [
    percentile(sorted, 50),
    percentile(sorted, 75),
    percentile(sorted, 99),
    sorted[sorted.length - 1],
  ];
}

function leftTailStats(values: number[]): number[] {
  if (values.length === 0) {
    return [];
  }
  const sorted = sortedStats(values);
  return [
    percentile(sorted, 50),
    percentile(sorted, 25),
    percentile(sorted, 1),
    sorted[0],
  ];
}

export function deriveNetActivity(
  subscriptions: SubRedDateValueRow[] | undefined,
  redemptions: SubRedDateValueRow[] | undefined,
): SubRedDateValueRow[] {
  const subMap = seriesToMap(subscriptions);
  const redMap = seriesToMap(redemptions);
  const dates = Array.from(
    new Set([...subMap.keys(), ...redMap.keys()]),
  ).sort();
  return [
    ['Date', 'Value'],
    ...dates.map(
      (date): SubRedDateValueRow => [
        date,
        (subMap.get(date) ?? 0) + (redMap.get(date) ?? 0),
      ],
    ),
  ];
}

export function resolveChartSeries(
  data: SubscriptionsRedemptionsReport,
  view: SubRedPanelView,
): SubRedDateValueRow[] {
  switch (view) {
    case 'redemption':
      return data.redemptions ?? [];
    case 'subscription':
      return data.subscriptions ?? [];
    case 'gross':
      return data.gross_activity ?? [];
    case 'net':
      return (
        data.net_activity ??
        deriveNetActivity(data.subscriptions, data.redemptions)
      );
  }
}

export function resolveStatisticalData(
  data: SubscriptionsRedemptionsReport,
  view: SubRedPanelView,
): number[] {
  switch (view) {
    case 'redemption':
      return data.statistical_redemption_data ?? [];
    case 'subscription':
      return (
        data.statistical_subscription_data ??
        rightTailStats(numericSeriesValues(data.subscriptions))
      );
    case 'gross':
      return data.statistical_gross_data ?? [];
    case 'net':
      return (
        data.statistical_net_data ??
        leftTailStats(numericSeriesValues(resolveChartSeries(data, 'net')))
      );
  }
}

export function resolveMetrics(
  data: SubscriptionsRedemptionsReport,
  view: SubRedPanelView,
): string[] {
  const isLeftTail = view === 'redemption' || view === 'net';
  const base = isLeftTail ? LEFT_TAIL_METRICS : RIGHT_TAIL_METRICS;
  const apiMetrics = data.statistical_metric ?? [];
  if (view === 'redemption' && apiMetrics.length > base.length) {
    return [...base, ...apiMetrics.slice(base.length)];
  }
  return base;
}
